import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { CheckCircle2, FileText, AlertCircle, RotateCcw } from "lucide-react";
import { toast } from "@/lib/toast";

/** Public signature page — accessible without auth via /sign/:token.
 *  Handles permits, change orders, completion sign-offs (anything created
 *  via POST /api/signables).
 */
export default function SignPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const canvasRef = useRef(null);
  const [drawing, setDrawing] = useState(false);
  const [hasInk, setHasInk] = useState(false);

  useEffect(() => {
    api.get(`/public/signables/${token}`)
      .then((r) => setDoc(r.data))
      .catch(() => setDoc(false))
      .finally(() => setLoading(false));
  }, [token]);

  useEffect(() => {
    if (!canvasRef.current) return;
    const c = canvasRef.current;
    const ctx = c.getContext("2d");
    ctx.lineCap = "round"; ctx.lineJoin = "round";
    ctx.strokeStyle = "#0A2540"; ctx.lineWidth = 2.5;
  }, [doc]);

  const xy = (e) => {
    const r = canvasRef.current.getBoundingClientRect();
    const t = e.touches ? e.touches[0] : e;
    return { x: t.clientX - r.left, y: t.clientY - r.top };
  };
  const start = (e) => { e.preventDefault(); setDrawing(true); const p = xy(e); const ctx = canvasRef.current.getContext("2d"); ctx.beginPath(); ctx.moveTo(p.x, p.y); };
  const move  = (e) => { if (!drawing) return; e.preventDefault(); const p = xy(e); const ctx = canvasRef.current.getContext("2d"); ctx.lineTo(p.x, p.y); ctx.stroke(); setHasInk(true); };
  const end   = () => setDrawing(false);
  const clear = () => { const c = canvasRef.current; c.getContext("2d").clearRect(0, 0, c.width, c.height); setHasInk(false); };

  const submit = async () => {
    if (!name.trim()) { toast.error("Type your full name"); return; }
    if (!hasInk) { toast.error("Sign in the box first"); return; }
    setSubmitting(true);
    try {
      const dataUrl = canvasRef.current.toDataURL("image/png");
      await api.post(`/public/signables/${token}/sign`, { name: name.trim(), signature_data_url: dataUrl });
      toast.success("Signed!");
      setDoc({ ...doc, status: "signed", signed_name: name.trim() });
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not submit"); }
    finally { setSubmitting(false); }
  };

  if (loading) return <Centered>Loading…</Centered>;
  if (doc === false) return <Centered>
    <AlertCircle className="w-10 h-10 text-rose-500 mx-auto mb-3" />
    <h1 className="font-heading text-xl text-[#0A2540]">Link not found or expired</h1>
    <p className="text-sm text-[#475569] mt-2">Please contact PowerBuilt for a fresh link.</p>
  </Centered>;

  return (
    <div className="min-h-screen bg-[#F8FAFC] dark:bg-slate-950 py-8 px-4" data-testid="sign-page">
      <div className="max-w-2xl mx-auto card-pb overflow-hidden">
        <div className="bg-gradient-to-r from-[#0A2540] to-[#1E3A66] text-white p-6">
          <div className="flex items-center gap-2 text-[#C9A356] text-xs uppercase tracking-widest mb-1">
            <FileText className="w-3 h-3" /> {doc.kind?.replace("_", " ") || "Document"}
          </div>
          <h1 className="font-heading text-2xl">{doc.title}</h1>
        </div>
        <div className="p-6">
          <div className="text-sm text-[#475569] dark:text-slate-300 whitespace-pre-line mb-6 max-h-[300px] overflow-y-auto" data-testid="sign-body">
            {doc.body}
          </div>

          {doc.status === "signed" ? (
            <div className="text-center py-8" data-testid="sign-done">
              <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto mb-3" />
              <h2 className="font-heading text-xl text-[#0A2540]">Signed</h2>
              <p className="text-sm text-[#475569] mt-1">{doc.signed_name} signed this {doc.kind} on {new Date(doc.signed_at).toLocaleDateString()}.</p>
              <button onClick={() => navigate("/")} className="btn-outline-navy text-sm mt-6">Back to home</button>
            </div>
          ) : (
            <>
              <label className="block">
                <span className="text-[11px] uppercase tracking-wider text-[#475569] font-medium mb-1 block">Your full name</span>
                <input value={name} onChange={(e) => setName(e.target.value)}
                       data-testid="sign-name"
                       placeholder="Sarah Williams"
                       className="w-full border border-[#E2E8F0] dark:border-slate-700 rounded-md px-3 py-2 bg-white dark:bg-slate-900 text-sm outline-none focus:ring-2 focus:ring-[#C9A356]/40 focus:border-[#C9A356]" />
              </label>

              <div className="mt-4">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-[11px] uppercase tracking-wider text-[#475569] font-medium">Sign below</span>
                  <button onClick={clear} className="text-xs text-[#C9A356] hover:underline inline-flex items-center gap-1" data-testid="sign-clear"><RotateCcw className="w-3 h-3" /> Clear</button>
                </div>
                <canvas
                  ref={canvasRef}
                  width={600} height={200}
                  data-testid="sign-canvas"
                  onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
                  onTouchStart={start} onTouchMove={move} onTouchEnd={end}
                  className="w-full bg-white border-2 border-dashed border-[#E2E8F0] dark:border-slate-700 rounded-md cursor-crosshair touch-none"
                  style={{ touchAction: "none" }}
                />
              </div>

              <button onClick={submit} disabled={submitting} className="btn-accent w-full mt-5" data-testid="sign-submit">
                {submitting ? "Submitting…" : "Sign & submit"}
              </button>
              <p className="text-[10px] text-center text-[#94A3B8] mt-2">
                By signing you agree this is your legal electronic signature.
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function Centered({ children }) {
  return (
    <div className="min-h-screen grid place-items-center px-4 text-center bg-[#F8FAFC]">
      <div className="card-pb p-8 max-w-sm">{children}</div>
    </div>
  );
}
